import { Route, Routes, Navigate, Outlet } from "react-router-dom";
import { useEffect, useState, useContext } from "react";
import { UserContext } from "../context/UserContext";
import { isCrmSalesUser } from "../utils/roles";
import Loader from "../components/Global/Loader";

import Home from "../pages/Home";
import LoginPage from "../pages/Login";
import SignupPage from "../pages/Signup";
import ForgotPassword from "../pages/ForgotPassword";
import VerifyOTP from "../pages/VerifyOTP";

import NotFound from "../pages/NotFound";

import StudentLayout from "../components/Student/Layout/Layout";
import ConsultantLayout from "../components/Consultant/Layout/Layout";
import PersonalInformation from "../pages/Student/PersonalInformation";
import StudentApplicationForm from "../pages/Student/StudentApplicationForm";
import StudentDashboard from "../pages/Student/StudentDashboard";
import StudentConsultations from "../pages/Student/StudentConsultations";
import StudentApplicationDetails from "../pages/Student/ViewApplication";

import ConsultantDashboard from "../pages/Consultant/ConsultantDashboard";
import ConsultantApplications from "../pages/Consultant/Applications";
import ConsultantApplicationDetails from "../pages/Consultant/ApplicationDetails";

import Layout from "../components/Admin/Layout/Layout";
import AddConsultant from "../pages/Admin/AddConsultant";
import AddUni from "../pages/Admin/AddUni";
import Dashboard from "../pages/Admin/Dashboard";
import CalculatorTable from "../pages/Admin/CalculatorTable";
import Calculator from "../pages/Admin/Calculator";

import Consultations from "../pages/Admin/Consultations";
import Applications from "../pages/Admin/Applications";
import AdminViewApplication from "../pages/Admin/ApplicationDetails";
import ConsultantConsultations from "../pages/Consultant/ConsultantConsultations";
import Universities from "../pages/Admin/Universities";
import Consultants from "../pages/Admin/Consultants";
import Students from "../pages/Admin/Students";
import StudentRecord from "../pages/Admin/StudentRecord";
import CRM from "../pages/Admin/CRM";
import CRMBoard from "../pages/Admin/CRMBoard";
import CRMDashboard from "../pages/Admin/CRMDashboard";
import SalesPersons from "../pages/Admin/SalesPersons";

import SalesPersonLayout from "../components/SalesPerson/Layout/Layout";
import SalesPersonAccount from "../pages/SalesPerson/SalesPersonAccount";

const Router = () => {
  // Consume values from UserContext
  const { userRole, setUserRole, verificationEmail, hasSalesPersonRole } = useContext(UserContext);
  const [isLoading, setIsLoading] = useState(true);
  const [isExpired, setIsExpired] = useState(false);

  const handleLogout = () => {
    // Clear user session data (context and storage)
    localStorage.clear();
    sessionStorage.clear();
    setUserRole("");

    // Replace the history state to prevent back navigation
    window.history.replaceState(null, null, "/");

    // Forcefully reload the page to clear all states
    setTimeout(() => {
      window.location.href = "/";
    }, 100); // Slight delay to ensure changes take effect
  };

  const handleTokenExpiry = () => {
    const token = localStorage.getItem("token");
    if (token) {
      // Decode JWT payload
      const { exp } = JSON.parse(atob(token.split(".")[1]));
      const expiryTime = exp * 1000 - Date.now();

      if (expiryTime <= 0) {
        setIsExpired(true);
      } else {
        const timeout = setTimeout(() => {
          setIsExpired(true);
        }, expiryTime);
        return () => clearTimeout(timeout);
      }
    }
  };

  useEffect(() => {
    setIsLoading(false);
    handleTokenExpiry();
  }, []);

  const checkAccess = (allowedRoles) => {
    if (!userRole) return false;
    // Check token expiry on each access check
    handleTokenExpiry();
    return allowedRoles.some(
      (role) => userRole.toLowerCase() === role.toLowerCase(),
    );
  };

  // A custom component to handle the root redirection.
  // If the user is logged in (has a token and role), redirect to their dashboard.
  // Otherwise, redirect to the signin page.
  const RootRedirector = () => {
    if (userRole) {
      if (userRole.toLowerCase() === "student") {
        return <Navigate to="/dashboard" replace />;
      } else if (userRole.toLowerCase() === "consultant") {
        return <Navigate to="/consultant/dashboard" replace />;
      } else if (userRole.toLowerCase() === "admin") {
        return <Navigate to="/admin/dashboard" replace />;
      } else if (userRole.toLowerCase() === "salesperson") {
        return <Navigate to="/sales/crm" replace />;
      }
    }
    return <Navigate to="/signin" replace />;
  };

  const ProtectedWrapper = ({ allowedRoles, children }) => {
    if (isLoading) return <Loader />;

    // If no user is found in context, redirect to the sign-in page
    if (!userRole) {
      return <Navigate to="/signin" replace />;
    }

    // Check if user is allowed access to this route
    if (!checkAccess(allowedRoles)) {
      return <NotFound />;
    }

    return children ? children : <Outlet />;
  };

  const PublicWrapper = ({ children }) => {
    if (isLoading) return <Loader />;

    // If there is a logged-in user, redirect them based on their role
    if (userRole) {
      if (userRole.toLowerCase() === "student") {
        return <Navigate to="/dashboard" replace />;
      } else if (userRole.toLowerCase() === "consultant") {
        return <Navigate to="/consultant/dashboard" replace />;
      } else if (userRole.toLowerCase() === "admin") {
        return <Navigate to="/admin/dashboard" replace />;
      } else if (userRole.toLowerCase() === "salesperson") {
        return <Navigate to="/sales/crm" replace />;
      }
    }

    return children;
  };

  return (
    <>
      {isExpired && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white w-full max-w-md mx-auto rounded-lg shadow-lg p-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">
              Session Timeout
            </h2>
            <div className="text-sm text-gray-600 mb-6">
              Your session has expired. Please log in again to continue.
            </div>
            <div className="flex justify-end space-x-4">
              <button
                onClick={() => handleLogout()}
                className="bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-blue-600 focus:outline-none transition-colors duration-200"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      <Routes>
        {/* Root path redirection */}
        <Route path="/" element={<Home />} />

        {/* Public Routes */}
        <Route
          path="/signin"
          element={
            <PublicWrapper>
              <LoginPage />
            </PublicWrapper>
          }
        />
        <Route
          path="/signup"
          element={
            <PublicWrapper>
              <SignupPage />
            </PublicWrapper>
          }
        />
        <Route
          path="/forgot-password"
          element={
            <PublicWrapper>
              <ForgotPassword />
            </PublicWrapper>
          }
        />

        <Route
          path="/verify-otp"
          element={
            <PublicWrapper>
              {verificationEmail ? (
                <VerifyOTP />
              ) : userRole ? (
                // Redirect logged-in users to their respective dashboard
                userRole.toLowerCase() === "student" ? (
                  <Navigate to="/dashboard" replace />
                ) : userRole.toLowerCase() === "consultant" ? (
                  <Navigate to="/consultant/dashboard" replace />
                ) : userRole.toLowerCase() === "admin" ? (
                  <Navigate to="/admin/dashboard" replace />
                ) : userRole.toLowerCase() === "salesperson" ? (
                  <Navigate to="/sales/crm" replace />
                ) : (
                  <Navigate to="/signin" replace />
                )
              ) : (
                // Redirect to signup page if no email is verified and no role
                <Navigate to="/signin" replace />
              )}
            </PublicWrapper>
          }
        />

        {/* Student Routes */}
        <Route
          element={
            <ProtectedWrapper allowedRoles={["Student"]}>
              <StudentLayout />
            </ProtectedWrapper>
          }
        >
          <Route path="/dashboard" element={<StudentDashboard />} />
          <Route path="/profile" element={<PersonalInformation />} />
          <Route path="/consultations" element={<StudentConsultations />} />
          <Route path="/application" element={<StudentApplicationForm />} />
          <Route
            path="/application-details"
            element={<StudentApplicationDetails />}
          />
        </Route>

        {/* Consultant Routes */}
        <Route
          element={
            <ProtectedWrapper allowedRoles={["Consultant"]}>
              <ConsultantLayout />
            </ProtectedWrapper>
          }
        >
          <Route
            path="/consultant/dashboard"
            element={<ConsultantDashboard />}
          />
          <Route
            path="/consultant/applications"
            element={<ConsultantApplications />}
          />
          <Route
            path="/consultant/application-form"
            element={<ConsultantApplicationDetails />}
          />
          <Route
            path="/consultant/consultations"
            element={<ConsultantConsultations />}
          />
          <Route
            path="/consultant/crm"
            element={
              isCrmSalesUser({ userRole, hasSalesPersonRole }) ? (
                <CRM />
              ) : (
                <Navigate to="/consultant/dashboard" replace />
              )
            }
          />
          <Route
            path="/consultant/crm/:boardId"
            element={
              isCrmSalesUser({ userRole, hasSalesPersonRole }) ? (
                <CRMBoard />
              ) : (
                <Navigate to="/consultant/dashboard" replace />
              )
            }
          />
        </Route>

        {/* Admin Routes */}
        <Route
          element={
            <ProtectedWrapper allowedRoles={["Admin"]}>
              <Layout />
            </ProtectedWrapper>
          }
        >
          <Route path="/admin/addconsultant" element={<AddConsultant />} />
          <Route path="/admin/consultants" element={<Consultants />} />
          <Route path="/admin/consultants/add" element={<AddConsultant />} />
          <Route
            path="/admin/consultants/add/:id"
            element={<AddConsultant />}
          />
          <Route path="/admin/applications" element={<Applications />} />
          <Route
            path="/admin/application-details"
            element={<AdminViewApplication />}
          />
          <Route path="/admin/adduni" element={<AddUni />} />
          <Route path="/admin/universities" element={<Universities />} />
          <Route path="/admin/dashboard" element={<Dashboard />} />
          <Route path="/admin/calculator" element={<CalculatorTable />} />
          <Route path="/admin/calculator-details" element={<Calculator />} />
          <Route path="/admin/students" element={<Students />} />
          <Route path="/admin/consultations" element={<Consultations />} />
          <Route path="/admin/student-record" element={<StudentRecord />} />
          <Route path="/admin/crm" element={<CRM />} />
          <Route path="/admin/crm/:boardId/dashboard" element={<CRMDashboard />} />
          <Route path="/admin/crm/:boardId" element={<CRMBoard />} />
          <Route path="/admin/sales-persons" element={<SalesPersons />} />
        </Route>

        {/* Sales Person Routes */}
        <Route
          element={
            <ProtectedWrapper allowedRoles={["SalesPerson"]}>
              <SalesPersonLayout />
            </ProtectedWrapper>
          }
        >
          <Route path="/sales/crm" element={<CRM />} />
          <Route path="/sales/crm/:boardId" element={<CRMBoard />} />
          <Route path="/sales/account" element={<SalesPersonAccount />} />
        </Route>

        {/* Catch all unauthorized routes */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </>
  );
};

export default Router;
